import { useEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { signImageUrls, cachedSignedUrl } from '../../lib/postImages';
import { useModalDismiss } from '../../hooks/useModalDismiss';

// Photos attached to a post or reply (migration 041). `images` is the jsonb
// array the read RPCs return: [{ path, thumb, w, h, alt }].
//
// The bucket is private, so each file needs a signed URL; requests from every
// card on screen are batched into one call by lib/postImages.
//
// Tiles reserve their space from the stored width/height, so the feed doesn't
// jump as photos arrive — the same reason the hero carousel is fixed-height.

const useSigned = (paths) => {
  const key = paths.join('|');
  const [urls, setUrls] = useState(() => {
    const seed = {};
    paths.forEach((p) => { const u = cachedSignedUrl(p); if (u) seed[p] = u; });
    return seed;
  });

  useEffect(() => {
    if (!paths.length) return;
    let cancelled = false;
    signImageUrls(paths).then((map) => { if (!cancelled) setUrls((prev) => ({ ...prev, ...map })); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return urls;
};

const Lightbox = ({ images, index, onClose, onStep }) => {
  const image = images[index];
  const urls = useSigned([image.path]);
  useModalDismiss(onClose);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') onStep(1);
      if (e.key === 'ArrowLeft') onStep(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onStep]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90" onClick={onClose}>
      <button
        onClick={onClose}
        aria-label="Close photo"
        className="absolute top-4 right-4 p-2 rounded-full text-white/80 hover:text-white"
        style={{ backgroundColor: 'rgba(255,255,255,0.12)', top: 'calc(1rem + var(--sat))' }}
      >
        <X size={22} />
      </button>

      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onStep(-1); }}
            aria-label="Previous photo"
            className="absolute left-2 p-2 rounded-full text-white/80 hover:text-white"
            style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onStep(1); }}
            aria-label="Next photo"
            className="absolute right-2 p-2 rounded-full text-white/80 hover:text-white"
            style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}

      {urls[image.path] && (
        <img
          src={urls[image.path]}
          alt={image.alt || ''}
          onClick={(e) => e.stopPropagation()}
          className="max-h-[90vh] max-w-[92vw] object-contain"
        />
      )}
      {images.length > 1 && (
        <p className="absolute bottom-4 text-xs text-white/70" style={{ bottom: 'calc(1rem + var(--sab))' }}>
          {index + 1} / {images.length}
        </p>
      )}
    </div>
  );
};

const PostImages = ({ images, className = '' }) => {
  const list = Array.isArray(images) ? images.filter((i) => i?.path && i?.thumb) : [];
  const [open, setOpen] = useState(null);
  const urls = useSigned(list.map((i) => i.thumb));

  const step = useCallback((delta) => {
    setOpen((prev) => (prev === null ? prev : (prev + delta + list.length) % list.length));
  }, [list.length]);

  if (!list.length) return null;

  // One photo keeps its own shape (capped so a tall photo can't run the card
  // off the screen); two or more go into an even grid.
  const single = list.length === 1;

  return (
    <>
      <div
        className={`grid gap-1.5 ${single ? 'grid-cols-1' : 'grid-cols-2'} ${className}`}
      >
        {list.map((image, i) => {
          const ratio = image.w && image.h ? image.w / image.h : 4 / 3;
          return (
            <button
              key={image.path}
              onClick={(e) => { e.stopPropagation(); setOpen(i); }}
              className="relative block w-full overflow-hidden rounded-xl transition-all active:scale-[0.99]"
              style={{
                aspectRatio: single ? `${Math.min(Math.max(ratio, 0.75), 1.9)}` : '1 / 1',
                backgroundColor: 'var(--skeleton-bg)',
              }}
              aria-label={image.alt ? `View photo: ${image.alt}` : `View photo ${i + 1}`}
            >
              {urls[image.thumb] && (
                <img
                  src={urls[image.thumb]}
                  alt={image.alt || ''}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full"
                  style={{ objectFit: single ? 'contain' : 'cover' }}
                />
              )}
            </button>
          );
        })}
      </div>

      {open !== null && (
        <Lightbox images={list} index={open} onClose={() => setOpen(null)} onStep={step} />
      )}
    </>
  );
};

export default PostImages;
